import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PageLoading } from '@/shared/components/Common/PageLoading';
import { AdminLayout } from '@/shared/components/Layout/AdminLayout';
import { ROUTES } from '@/shared/constants/routes';

// 懒加载页面组件
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const UsersPage = lazy(() => import('@/features/system/pages/UsersPage'));
const RolesPage = lazy(() => import('@/features/system/pages/RolesPage'));
const PermissionsPage = lazy(() => import('@/features/system/pages/PermissionsPage'));
const AuditLogsPage = lazy(() => import('@/features/system/pages/AuditLogsPage'));
const AnalyticsPage = lazy(() => import('@/features/system/pages/AnalyticsPage'));
const DataAnalyticsPage = lazy(() => import('@/features/system/pages/DataAnalyticsPage'));
const BannersPage = lazy(() => import('@/features/content/pages/BannersPage'));
const NewsPage = lazy(() => import('@/features/content/pages/NewsPage'));
const PartnersPage = lazy(() => import('@/features/content/pages/PartnersPage'));
const JobsPage = lazy(() => import('@/features/content/pages/JobsPage'));
const SocialRecruitmentPage = lazy(() => import('@/features/content/pages/SocialRecruitmentPage'));
const CampusRecruitmentPage = lazy(() => import('@/features/content/pages/CampusRecruitmentPage'));
const CitiesPage = lazy(() => import('@/features/content/pages/CitiesPage'));
const JobTypesPage = lazy(() => import('@/features/content/pages/JobTypesPage'));
const FeedbackPage = lazy(() => import('@/features/content/pages/FeedbackPage'));
const FinancialReportsPage = lazy(() => import('@/features/investor/pages/FinancialReportsPage'));
const GovernancePage = lazy(() => import('@/features/investor/pages/GovernancePage'));
const ReportingChannelPage = lazy(() => import('@/features/investor/pages/ReportingChannelPage'));
const TeamGalleryPage = lazy(() => import('@/features/content/pages/TeamGalleryPage'));
const AnnouncementsPage = lazy(() => import('@/features/investor/pages/AnnouncementsPage'));
const MilestonesPage = lazy(() => import('@/features/about/pages/MilestonesPage'));
const HonorsPage = lazy(() => import('@/features/about/pages/HonorsPage'));
const OrgStructurePage = lazy(() => import('@/features/about/pages/OrgStructurePage'));
const CompaniesPage = lazy(() => import('@/features/about/pages/CompaniesPage'));
const AboutContentPage = lazy(() => import('@/features/content/pages/AboutContentPage'));
const AdvantagesPage = lazy(() => import('@/features/content/pages/AdvantagesPage'));

// 路由守卫组件
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  console.log('🛡️ [ProtectedRoute] 路由守卫检查');
  const token = localStorage.getItem('admin_token');
  console.log('🔑 [ProtectedRoute] Token:', token ? '存在' : '不存在');
  console.log('📍 [ProtectedRoute] 当前路径:', window.location.pathname);
  
  if (!token) {
    console.log('❌ [ProtectedRoute] 未登录，重定向到登录页');
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  console.log('✅ [ProtectedRoute] 已登录，允许访问');
  return <>{children}</>;
};

// Suspense 包装器
const withSuspense = (Component: React.LazyExoticComponent<any>) => {
  return (
    <Suspense fallback={<PageLoading />}>
      <Component />
    </Suspense>
  );
};

export const router = createBrowserRouter(
  [
    {
      path: ROUTES.LOGIN,
      element: withSuspense(LoginPage),
    },
  {
    path: '/admin',
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: withSuspense(DashboardPage),
      },
      {
        path: 'users',
        element: withSuspense(UsersPage),
      },
      {
        path: 'roles',
        element: withSuspense(RolesPage),
      },
      {
        path: 'permissions',
        element: withSuspense(PermissionsPage),
      },
      {
        path: 'audit-logs',
        element: withSuspense(AuditLogsPage),
      },
      {
        path: 'analytics',
        element: withSuspense(AnalyticsPage),
      },
      {
        path: 'data-analytics',
        element: withSuspense(DataAnalyticsPage),
      },
      {
        path: 'banners',
        element: withSuspense(BannersPage),
      },
      {
        path: 'news',
        element: withSuspense(NewsPage),
      },
      {
        path: 'partners',
        element: withSuspense(PartnersPage),
      },
      {
        path: 'jobs',
        element: withSuspense(JobsPage),
      },
      {
        path: 'recruitment/social',
        element: withSuspense(SocialRecruitmentPage),
      },
      {
        path: 'recruitment/campus',
        element: withSuspense(CampusRecruitmentPage),
      },
      {
        path: 'recruitment/cities',
        element: withSuspense(CitiesPage),
      },
      {
        path: 'recruitment/job-types',
        element: withSuspense(JobTypesPage),
      },
      {
        path: 'feedback',
        element: withSuspense(FeedbackPage),
      },
      {
        path: 'financial-reports',
        element: withSuspense(FinancialReportsPage),
      },
      {
        path: 'governance',
        element: withSuspense(GovernancePage),
      },
      {
        path: 'reporting-channel',
        element: withSuspense(ReportingChannelPage),
      },
      {
        path: 'team-gallery',
        element: withSuspense(TeamGalleryPage),
      },
      {
        path: 'announcements',
        element: withSuspense(AnnouncementsPage),
      },
      {
        path: 'milestones',
        element: withSuspense(MilestonesPage),
      },
      {
        path: 'honors',
        element: withSuspense(HonorsPage),
      },
      {
        path: 'org-structure',
        element: withSuspense(OrgStructurePage),
      },
      {
        path: 'companies',
        element: withSuspense(CompaniesPage),
      },
      {
        path: 'content/home-about',
        element: withSuspense(AboutContentPage),
      },
      {
        path: 'content/about-company-intro',
        element: withSuspense(AboutContentPage),
      },
      {
        path: 'about-content',
        element: <Navigate to="/admin/content/home-about" replace />,
      },
      {
        path: 'advantages',
        element: withSuspense(AdvantagesPage),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },
],
{
  future: {
    v7_startTransition: true,
  } as any,
}
);
